import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CalendarClock, Download, Plus, Search } from 'lucide-react'
import { getAssets, previewPostDue } from '../../api/operatingCosts'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { money, num } from '../sales/salesShared'
import AccountingTabs from './AccountingTabs'
import DepreciationReviewModal from './DepreciationReviewModal'
import { monthName } from './accountingShared'

const SHOW = [
  { id: 'active', label: 'Active' },
  { id: 'due', label: 'Depreciation due' },
  { id: 'paused', label: 'Paused' },
  { id: 'disposed', label: 'Disposed' },
  { id: 'all', label: 'All' },
]
const STATUS_TONE = { Active: 'good', Paused: 'warn', Disposed: 'muted' }

// The fixed asset register: what the business owns, what it cost, and what
// it's worth in the books after depreciation.
export default function Assets() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const show = SHOW.some((s) => s.id === params.get('show')) ? params.get('show') : 'active'
  const [rows, setRows] = useState(null)
  const [due, setDue] = useState(new Set())
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [reviewOpen, setReviewOpen] = useState(false)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    getAssets().then((d) => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]))
    previewPostDue().then((p) => setDue(new Set((p?.included || []).map((a) => a.asset_id)))).catch(() => setDue(new Set()))
  }, [version])

  const inShow = (r, id) => (id === 'all') || (id === 'due' ? due.has(r.asset_id) : String(r.status || 'Active').toLowerCase() === id)
  const counts = useMemo(() => Object.fromEntries(SHOW.map((s) => [s.id, (rows || []).filter((r) => inShow(r, s.id)).length])), [rows, due])
  const categories = useMemo(() => [...new Set((rows || []).map((r) => r.category).filter(Boolean))].sort(), [rows])
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (rows || []).filter((r) => inShow(r, show)
      && (!category || r.category === category)
      && (!term || [r.name, r.category, r.location_name, r.serial_number, r.department].join(' ').toLowerCase().includes(term)))
  }, [rows, show, category, search, due])
  const sum = (key) => shown.reduce((s, r) => s + num(r[key]), 0)

  const exportCsv = () => {
    const head = ['Name', 'Category', 'Department', 'Location', 'Status', 'Acquired', 'Cost', 'Residual value', 'Useful life (months)',
      'Accumulated depreciation', 'Net book value', 'Last posted', 'Asset account', 'Accumulated depreciation account', 'Expense account', 'Serial number']
    const lines = shown.map((r) => [r.name, r.category, r.department, r.location_name, r.status, String(r.acquisition_date || '').slice(0, 10),
      num(r.cost).toFixed(2), num(r.salvage_value).toFixed(2), r.useful_life_months, num(r.accumulated_depreciation).toFixed(2),
      num(r.net_book_value).toFixed(2), r.last_posted_ym, r.asset_account_code, r.accum_dep_account_code, r.expense_account_code, r.serial_number])
    const csv = [head, ...lines].map((a) => a.map((x) => `"${String(x ?? '').replaceAll('"', '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `fixed_assets_${localToday()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page">
      <AccountingTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Fixed assets <HelpTip k="fixedAssets" /></h1>
            <div className="ci-title-meta">What the business owns and depreciates.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!shown.length}><Download size={15} />Export</button>
          {due.size > 0 && <button data-write type="button" className="ci-btn ci-btn-ghost" onClick={() => setReviewOpen(true)}><CalendarClock size={15} />Post depreciation due</button>}
          <Link data-write className="ci-btn ci-btn-primary" to="/accounting/assets/new"><Plus size={15} />Add a fixed asset</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search fixed assets">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, location or serial number…" />
        </label>
        {categories.length > 0 && (
          <select className="ci-select" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      <FilterChips options={SHOW.map((s) => ({ ...s, count: counts[s.id] }))} value={show} onChange={(id) => setParams(id === 'active' ? {} : { show: id }, { replace: true })} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Asset</th>
                <th className="hide-sm">Acquired</th>
                <th className="num hide-sm">Cost</th>
                <th className="num hide-sm">Depreciated <HelpTip k="accumulatedDepreciation" /></th>
                <th className="num">Book value <HelpTip k="netBookValue" /></th>
                <th className="center">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr><td colSpan="6"><Spinner label="Loading fixed assets…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'No fixed assets here.' : 'No fixed assets yet.'}</div></td></tr>
              ) : shown.map((r) => (
                <tr key={r.asset_id} className="row-link" onClick={() => nav(`/accounting/assets/${r.asset_id}`)}>
                  <td>
                    <Link className="ci-strong acc-row-link" to={`/accounting/assets/${r.asset_id}`} onClick={(e) => e.stopPropagation()}>{r.name}</Link>
                    <span className="cell-sub">{[r.category, r.location_name, r.last_posted_ym && `posted to ${monthName(r.last_posted_ym)}`].filter(Boolean).join(' · ') || 'Not depreciated yet'}</span>
                  </td>
                  <td className="ci-muted-cell hide-sm">{fmtDay(r.acquisition_date)}</td>
                  <td className="num hide-sm">{money(r.cost)}</td>
                  <td className="num hide-sm">{money(r.accumulated_depreciation)}</td>
                  <td className="num ci-total">{money(r.net_book_value)}</td>
                  <td className="center">
                    <StatusBadge label={r.status || 'Active'} tone={STATUS_TONE[r.status] || 'good'} />
                    {due.has(r.asset_id) && <span className="cell-sub acc-due">Depreciation due</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">
            {shown.length} {shown.length === 1 ? 'asset' : 'assets'} · cost {money(sum('cost'))} · book value {money(sum('net_book_value'))}
          </span>
        </div>
      </div>

      <DepreciationReviewModal open={reviewOpen} assetId={null} onClose={() => setReviewOpen(false)} onPosted={() => setVersion((v) => v + 1)} />
    </div>
  )
}
